import { Layer, LayerProps } from 'shared/map-exports';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { StyleFunctionProps } from 'components/Map/utils';
import { useMapLayerEvents } from '../useMapLayerEvents';
import {
  ExternalFeatureLayerProps,
  ExternalFeatureProperties,
} from './ExternalFeatureLayer.types';
import { useLayerClickHandler } from './useLayerClickHandler';

type PointLayerProps = ExternalFeatureLayerProps;

export const getSelectedSymbolStyle = (
  props: StyleFunctionProps,
): LayerProps => {
  const { idKey, otherLayerProps = {}, selectedId } = props;
  return {
    ...otherLayerProps,
    type: 'symbol',
    layout: {
      'icon-image': 'CustomLayerLocationPin',
      'icon-allow-overlap': true,
      'icon-anchor': 'bottom',
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-color': '#52E8EB',
    },
    filter: ['==', idKey, selectedId],
  };
};

/**
 * Renders whatever layers are defined in the `displayStyleData` of the feature layer, and:
 * - Adds a selected symbol layer that highlight the currently selected symbol.
 * - Uses the `clickListenerLayerId` to handle click events on the symbols.
 */
export const EmbeddedStylePointLayer = (
  props: PointLayerProps,
): JSX.Element => {
  const {
    sourceId,
    featureLayer: { shortName, displayStyleData, displayName, geomType },
  } = props;
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();
  const clickHandler = useLayerClickHandler(sourceId, displayName, geomType);

  if (displayStyleData.displayStyleType !== 'embedded') {
    throw new Error(
      'EmbeddedStyle*Layer requires displayStyleType to be embedded',
    );
  }

  useMapLayerEvents<ExternalFeatureProperties>({
    layerId: displayStyleData.clickListenerLayerId,
    onClick: clickHandler,
  });

  const selectedId =
    (mapDrawerEntityForPoi?.type === sourceId && mapDrawerEntityForPoi?.id) ||
    '';
  const selectedSymbolStyle = getSelectedSymbolStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
    },
    selectedId,
  });

  return (
    <>
      {displayStyleData.layers
        // Add the `source` property to each layer's style so it references the correct
        // <Source>.
        .map((layer) => ({ ...layer, source: sourceId }) as LayerProps)
        .map((layer) => (
          <Layer key={layer.id} {...layer} />
        ))}
      <Layer {...selectedSymbolStyle} />
    </>
  );
};
